// Container Trader wholesale inventory: shared browser-side helpers.
//
// The option lists must match server/wholesale/validation.js (a test checks this). The server is
// the authority on who may see or change anything; these helpers only decide what to display.

export const SIZE_OPTIONS = [
  { value: '10', label: "10'" },
  { value: '20', label: "20'" },
  { value: '40', label: "40'" },
  { value: '45', label: "45'" },
  { value: '53', label: "53'" },
];

export const TYPE_OPTIONS = [
  { value: 'standard', label: 'Standard' },
  { value: 'high-cube', label: 'High cube' },
  { value: 'double-door', label: 'Double door' },
  { value: 'open-side', label: 'Open side' },
  { value: 'open-top', label: 'Open top' },
  { value: 'flat-rack', label: 'Flat rack' },
  { value: 'refrigerated', label: 'Refrigerated' },
  { value: 'duocon', label: "Duocon (two 10')" },
];

export const CONDITION_OPTIONS = [
  { value: 'new-one-trip', label: 'New / one trip' },
  { value: 'multi-trip', label: 'Multi-trip' },
  { value: 'iicl', label: 'IICL' },
  { value: 'cargo-worthy', label: 'Cargo worthy' },
  { value: 'wind-water-tight', label: 'Wind & water tight' },
  { value: 'as-is', label: 'As is' },
];

// Statuses a supplier can choose. "Allocated" is set by Container Trader during a purchase.
export const SUPPLIER_STATUS_OPTIONS = [
  { value: 'available', label: 'Available' },
  { value: 'paused', label: 'Paused (hidden from dealers)' },
  { value: 'sold', label: 'Sold' },
];

export const STATUS_LABELS = {
  available: 'Available',
  paused: 'Paused',
  allocated: 'Allocated to a purchase',
  sold: 'Sold',
};

export const US_STATES = 'AL AK AZ AR CA CO CT DE DC FL GA HI ID IL IN IA KS KY LA ME MD MA MI MN MS MO MT NE NV NH NJ NM NY NC ND OH OK OR PA RI SC SD TN TX UT VT VA WA WV WI WY'.split(
  ' '
);

// Canadian provinces and territories (no code clashes with a US state).
export const CA_PROVINCES = 'AB BC MB NB NL NS NT NU ON PE QC SK YT'.split(' ');
export const isCanadianRegion = v => CA_PROVINCES.includes(v);

// "t2p1j9" -> "T2P 1J9"; null when invalid.
export const canadianPostal = v => {
  const s = `${v || ''}`.toUpperCase().replace(/\s+/g, '');
  return /^[ABCEGHJ-NPRSTVXY]\d[ABCEGHJ-NPRSTV-Z]\d[ABCEGHJ-NPRSTV-Z]\d$/.test(s)
    ? `${s.slice(0, 3)} ${s.slice(3)}`
    : null;
};

export const MAX_TEXT = 2000;
export const MAX_DETAILS = 300;

const labelFrom = (options, value) => (options.find(o => o.value === value) || {}).label || value;

export const sizeLabel = v => labelFrom(SIZE_OPTIONS, v);
export const typeLabel = v => labelFrom(TYPE_OPTIONS, v);
export const conditionLabel = v => labelFrom(CONDITION_OPTIONS, v);
export const statusLabel = v => STATUS_LABELS[v] || v;

// "40' High cube, Cargo worthy"
export const itemTitle = item =>
  item
    ? `${sizeLabel(item.containerSize)} ${typeLabel(item.containerType)}, ${conditionLabel(
        item.condition
      )}`
    : '';

export const formatCents = (cents, currency = 'USD') => {
  const n = Number(cents);
  if (!Number.isFinite(n)) return '';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
    minimumFractionDigits: n % 100 === 0 ? 0 : 2,
  }).format(n / 100);
};

export const formatDate = iso => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  } catch (e) {
    return iso;
  }
};

// Display-only hint for showing navigation. The server re-checks on every request.
export const isApprovedSupplier = currentUser =>
  `${currentUser?.attributes?.profile?.metadata?.ctWholesaleSupplier || ''}`
    .trim()
    .toLowerCase() === 'approved';

// ---- Form <-> API ------------------------------------------------------------------------------

export const EMPTY_FORM = {
  containerSize: '',
  containerType: '',
  condition: '',
  city: '',
  state: '',
  zip: '',
  quantityAvailable: '1',
  wholesalePrice: '',
  pickupTerms: '',
  releaseTerms: '',
  details: '',
  supplierStockId: '',
  containerNumber: '',
  status: 'available',
  stockVerified: false,
};

export const itemToForm = item => ({
  ...EMPTY_FORM,
  containerSize: item.containerSize || '',
  containerType: item.containerType || '',
  condition: item.condition || '',
  city: item.location?.city || '',
  state: item.location?.state || '',
  zip: item.location?.zip || '',
  quantityAvailable: `${item.quantityAvailable ?? ''}`,
  wholesalePrice:
    item.wholesalePriceCents != null ? (Number(item.wholesalePriceCents) / 100).toFixed(2) : '',
  pickupTerms: item.pickupTerms || '',
  releaseTerms: item.releaseTerms || '',
  details: item.details || '',
  supplierStockId: item.supplierStockId || '',
  containerNumber: item.containerNumber || '',
  status: item.status || 'available',
  stockVerified: false,
});

const centsFromDollars = v => {
  const s = `${v || ''}`.replace(/[$,\s]/g, '');
  if (!/^\d+(\.\d{1,2})?$/.test(s)) return NaN;
  return Math.round(Number(s) * 100);
};

/**
 * Client-side checks so suppliers see problems before submitting. Mirrors the server rules;
 * the server still validates everything. Returns { fieldName: message }.
 */
export const validateForm = (form, { mode }) => {
  const errors = {};
  if (!form.containerSize) errors.containerSize = 'Choose a size.';
  if (!form.containerType) errors.containerType = 'Choose a type.';
  if (!form.condition) errors.condition = 'Choose a condition.';
  if (!form.city.trim()) errors.city = 'Enter the pickup city.';
  else if (form.city.trim().length > 100) errors.city = 'Use 100 characters or fewer.';
  if (isCanadianRegion(form.state)) {
    if (!canadianPostal(form.zip)) errors.zip = 'Enter a postal code, for example T2P 1J9.';
  } else {
    if (!US_STATES.includes(form.state)) errors.state = 'Choose a state or province.';
    if (!/^\d{5}$/.test(form.zip.trim())) errors.zip = 'Enter a 5-digit ZIP.';
  }
  const qty = Number(form.quantityAvailable);
  if (!Number.isInteger(qty) || qty < 0 || qty > 1000) {
    errors.quantityAvailable = 'Enter a whole number from 0 to 1,000.';
  }
  const cents = centsFromDollars(form.wholesalePrice);
  if (!Number.isInteger(cents) || cents <= 0 || cents > 100000000) {
    errors.wholesalePrice = 'Enter a price in dollars, for example 2100 or 2100.50.';
  }
  if (form.pickupTerms.length > MAX_TEXT) errors.pickupTerms = 'Use 2,000 characters or fewer.';
  if (form.releaseTerms.length > MAX_TEXT) errors.releaseTerms = 'Use 2,000 characters or fewer.';
  if (form.details.trim().length > MAX_DETAILS) errors.details = 'Use 300 characters or fewer.';
  if (form.supplierStockId.trim().length > 100) {
    errors.supplierStockId = 'Use 100 characters or fewer.';
  }
  const cn = form.containerNumber.toUpperCase().replace(/\s/g, '');
  if (cn && !/^[A-Z]{4}\d{7}$/.test(cn)) {
    errors.containerNumber = 'Use 4 letters and 7 digits, for example ABCU1234567.';
  }
  if (mode === 'update' && !SUPPLIER_STATUS_OPTIONS.some(o => o.value === form.status)) {
    if (form.status !== 'allocated') errors.status = 'Choose a status.';
  }
  return errors;
};

// Builds the JSON body for POST/PATCH /api/wholesale/items.
export const formToBody = (form, { mode }) => {
  const body = {
    containerSize: form.containerSize,
    containerType: form.containerType,
    condition: form.condition,
    location: { city: form.city.trim(), state: form.state, zip: form.zip.trim() },
    quantityAvailable: Number(form.quantityAvailable),
    wholesalePrice: `${form.wholesalePrice}`.trim(),
    pickupTerms: form.pickupTerms,
    releaseTerms: form.releaseTerms,
    details: form.details.trim(),
    supplierStockId: form.supplierStockId,
    containerNumber: form.containerNumber,
  };
  if (mode === 'update' && form.status !== 'allocated') body.status = form.status;
  if (form.stockVerified) body.stockVerified = true;
  return body;
};

// Server field names -> form field names, for showing server-side validation errors.
export const serverFieldToFormField = f =>
  ({
    'location.city': 'city',
    'location.state': 'state',
    'location.zip': 'zip',
    location: 'city',
  }[f] || f);

export const reasonMessage = reason =>
  ({
    'wholesale-disabled': 'The wholesale section is not open yet.',
    'signed-out': 'Please log in to manage wholesale inventory.',
    'account-inactive': 'Your account is not active. Contact admin@thecontainertrader.com.',
    'not-eligible':
      'Wholesale inventory is available to approved suppliers. Contact admin@thecontainertrader.com to apply.',
    'plan-inactive':
      'Your plan is not active. Contact admin@thecontainertrader.com to restore access.',
    'wholesale-unavailable': 'Wholesale inventory is temporarily unavailable. Try again shortly.',
    'not-owner': 'You can only edit your own inventory.',
    unavailable: 'This item could not be found.',
    'missing-resource': 'This item could not be found.',
    invalid: 'Please check the highlighted fields and try again.',
  }[reason] || 'Something went wrong. Please try again.');

// ---- Dealer side ---------------------------------------------------------------------------

// Display-only hint: active Dealer Pro. The server re-checks on every request.
export const isActiveDealerPro = currentUser => {
  const md = currentUser?.attributes?.profile?.metadata || {};
  const plan = `${md.ctSellerPlan || ''}`.trim().toLowerCase();
  const status = `${md.ctPlanStatus || ''}`.trim().toLowerCase() || 'active';
  const blocked = `${md.ctWholesaleBuyer || ''}`.trim().toLowerCase() === 'blocked';
  return plan === 'pro' && status === 'active' && !blocked;
};

export const REQUEST_STATUS_LABELS = {
  pending: 'Pending review',
  confirmed: 'Confirmed',
  cancelled: 'Cancelled',
  declined: 'Declined',
};

export const MAX_REQUEST_QUANTITY = 50;

export const validateRequestForm = ({ quantity, contactPhone, message }, maxQuantity) => {
  const errors = {};
  const q = Number(quantity);
  const max = Math.min(maxQuantity || 0, MAX_REQUEST_QUANTITY);
  if (!Number.isInteger(q) || q < 1 || q > max) errors.quantity = `Choose 1 to ${max}.`;
  const phone = `${contactPhone || ''}`.trim();
  if (phone && !/^[0-9+().\-\s]{7,25}$/.test(phone))
    errors.contactPhone = 'Enter a valid phone number.';
  if (`${message || ''}`.length > 1000) errors.message = 'Use 1,000 characters or fewer.';
  return errors;
};

// Browse filters <-> URL query string
export const BROWSE_FILTER_KEYS = ['state', 'size', 'type', 'condition', 'page'];
export const parseBrowseQuery = search => {
  const p = new URLSearchParams(search || '');
  const out = {};
  BROWSE_FILTER_KEYS.forEach(k => {
    const v = p.get(k);
    if (v) out[k] = v;
  });
  return out;
};
export const browseQueryString = filters => {
  const p = new URLSearchParams();
  BROWSE_FILTER_KEYS.forEach(k => {
    if (filters[k] && !(k === 'page' && `${filters[k]}` === '1')) p.set(k, filters[k]);
  });
  const s = p.toString();
  return s ? `?${s}` : '';
};

export const requestErrorMessage = e =>
  ({
    unavailable:
      'Those containers are no longer available in that quantity. Refresh and try again.',
    'too-many-pending':
      'You have 10 pending requests. Wait for those to be reviewed or cancel one first.',
    'not-pending': 'This request was already updated. Refresh to see its status.',
  }[(e?.error)] || reasonMessage(e?.reason || e?.error));

// Dealer-facing explanations for why wholesale is not shown.
export const dealerReasonMessage = reason =>
  ({
    'not-eligible':
      'Wholesale inventory is included with the Dealer Pro plan. Visit My plan to request an upgrade.',
    'plan-inactive':
      'Your Dealer Pro plan is not active right now, so wholesale inventory is hidden. Contact admin@thecontainertrader.com to restore access.',
  }[reason] || reasonMessage(reason));

// ---- Messages (see server/api/wholesale-messages.js) ----

export const MAX_MESSAGE = 2000;

// `from` is relative to the viewer: 'you' | 'dealer' | 'supplier' | 'container-trader'.
export const messageSender = (from, viewerRole) => {
  if (from === 'you') return 'You';
  if (from === 'container-trader') return 'Container Trader';
  if (from === 'dealer') return viewerRole === 'admin' ? 'Dealer' : 'Dealer Pro member';
  if (from === 'supplier') return 'Supplier';
  return '';
};

export const FLAG_LABELS = {
  email: 'email address',
  phone: 'phone number',
  link: 'link',
};

export const messageErrorMessage = e =>
  ({
    'rate-limited': 'You have sent a lot of messages in the last hour. Try again a little later.',
    'thread-full': 'This conversation is full. Contact admin@thecontainertrader.com.',
    'thread-closed': 'This request is closed, so new messages are turned off.',
    invalid:
      e?.fields && e.fields.includes('files')
        ? "That file couldn't be sent. Use PDF, Word or Excel files up to 8 MB each."
        : `Write a message of up to ${MAX_MESSAGE} characters, or add a photo or file.`,
    'too-many-photos': 'This request has reached its limit of 40 photos and files.',
    'too-large': 'Those attachments are too large to send together. Try fewer at a time.',
  }[e?.error || e?.reason] || reasonMessage(e?.reason || e?.error));

export const unreadLabel = n => (n > 0 ? `${n} new` : '');
